<template>
  <div class="wsl">
    <div class="wsl-bar">
      <b-form-checkbox v-model="pendingOnly" switch size="sm" class="wsl-toggle" @change="refresh">{{ $t('whatsappHub.scheduled.pendingOnly') }}</b-form-checkbox>
      <span class="wsl-bar__spacer"/>
      <button type="button" class="wsl-tool" v-b-tooltip.hover :title="$t('whatsappHub.reload')" @click="refresh">
        <i class="fas fa-redo" :class="{'fa-spin': loading}"/>
      </button>
    </div>

    <div v-if="loading && batches.length === 0" class="wsl-empty"><b-spinner small/></div>
    <div v-else-if="batches.length === 0" class="wsl-empty">
      <i class="far fa-clock"/>
      <p>{{ $t('whatsappHub.empty.scheduled') }}</p>
    </div>
    <div v-else class="wsl-list">
      <div v-for="batch in batches" :key="batch.batchId" class="wsl-batch" :class="'wsl-batch--' + batch.tone">
        <div class="wsl-batch__head">
          <span class="wsl-when tabular"><i class="far fa-clock mr-25"/>{{ batch.sendAt | moment('ddd DD.MM.YYYY HH:mm') }}</span>
          <span class="wsl-mode"><i :class="batch.mode === 'TEMPLATE' ? 'fas fa-file-lines' : batch.mode === 'MEDIA' ? 'fas fa-image' : 'fas fa-keyboard'"/>
            {{ batch.mode === 'TEMPLATE' ? (batch.templateName || $t('whatsappHub.composer.modeTemplate')) : $t('whatsappHub.composer.mode' + (batch.mode === 'MEDIA' ? 'Media' : 'Text')) }}
          </span>
          <span class="wsl-by">{{ $t('whatsappHub.scheduled.createdBy') }} {{ batch.createdBy }}</span>
          <span class="wsl-bar__spacer"/>
          <span v-for="(n, status) in batch.counts" :key="status" class="wsl-status" :data-status="status">{{ n }} {{ $t('whatsappHub.scheduled.status.' + status) }}</span>
          <b-button v-if="batch.counts.PENDING" variant="outline-danger" size="sm" @click="cancel(batch)">
            <i class="fas fa-ban mr-25"/>{{ $t('whatsappHub.scheduled.cancel') }}
          </b-button>
        </div>
        <p v-if="batch.body" class="wsl-body">{{ batch.body }}</p>
        <div class="wsl-people">
          <span v-for="item in batch.items" :key="item.id" class="wsl-person" :data-status="item.status"
                v-b-tooltip.hover :title="(item.phone || '') + (item.error ? ' · ' + item.error : '')">
            <i :class="item.status === 'SENT' ? 'fas fa-check' : item.status === 'FAILED' ? 'fas fa-triangle-exclamation' : item.status === 'CANCELLED' ? 'fas fa-ban' : 'far fa-clock'"/>
            {{ item.clientName }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions} from 'vuex'

/** Scheduled sends grouped by batch (one "send later" = one batch), cancellable while pending. */
export default {
  name: 'WhatsappScheduledList',
  props: {
    buid: {type: [Number, String], default: null},
  },
  data: () => ({
    items: [],
    loading: false,
    pendingOnly: true,
  }),
  computed: {
    batches() {
      const map = {};
      this.items.forEach(item => {
        const key = item.batchId || item.id;
        if (!map[key]) {
          const request = item.request || {};
          map[key] = {
            batchId: key, sendAt: item.sendAt, createdBy: item.createdBy, mode: request.mode || 'TEMPLATE',
            templateName: request.templateName, body: request.body, items: [], counts: {},
          };
        }
        map[key].items.push(item);
        map[key].counts[item.status] = (map[key].counts[item.status] || 0) + 1;
      });
      return Object.values(map).map(b => ({
        ...b,
        tone: b.counts.PENDING ? 'pending' : b.counts.FAILED ? 'failed' : b.counts.SENT ? 'sent' : 'cancelled',
      })).sort((a, b) => (a.sendAt || '') < (b.sendAt || '') ? -1 : 1);
    },
    pendingCount() {
      return this.items.filter(i => i.status === 'PENDING').length;
    },
  },
  watch: {
    buid() {
      this.refresh();
    },
  },
  created() {
    this.refresh();
  },
  methods: {
    ...mapActions('whatsapp', ['hubScheduled', 'hubCancelScheduled']),
    refresh() {
      if (!this.buid) return;
      this.loading = true;
      this.hubScheduled({buids: [this.buid], pendingOnly: this.pendingOnly}).then(items => {
        this.items = items;
        this.$emit('loaded', {pending: this.pendingCount});
      }).catch(error => {
        this.$store.dispatch('notifications/error', {title: {text: 'WhatsApp'}, message: error});
      }).finally(() => this.loading = false);
    },
    cancel(batch) {
      this.$bvModal.msgBoxConfirm(this.$t('whatsappHub.scheduled.cancelConfirm', {n: batch.counts.PENDING}), {
        title: this.$t('whatsappHub.scheduled.cancel'), okVariant: 'danger', okTitle: this.$t('whatsappHub.scheduled.cancel'),
        cancelTitle: this.$t('whatsappHub.composer.close'), centered: true,
      }).then(ok => {
        if (!ok) return;
        this.hubCancelScheduled({batchId: batch.batchId}).then(this.refresh);
      });
    },
  }
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }
.wsl-bar { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.wsl-bar__spacer { flex: 1; }
.wsl-toggle { margin: 0; font-size: .74rem; font-weight: 700; color: #64748b; white-space: nowrap; :deep(.custom-control-label) { cursor: pointer; } }
.wsl-tool {
  width: 30px; height: 30px; border: none; border-radius: 9px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; background: rgba(15, 23, 42, .05); color: #64748b; font-size: .74rem;
  &:focus { outline: none; }
}
.wsl-empty { text-align: center; padding: 48px 0 30px; color: #94a3b8; i { font-size: 2.4rem; opacity: .35; } p { margin-top: 8px; font-size: .86rem; } }
.wsl-list { display: flex; flex-direction: column; gap: 10px; }
.wsl-batch {
  border: 1px solid rgba(15, 23, 42, .08); border-left-width: 4px; border-radius: 12px; padding: 12px 14px; background: #fff;
  &--pending { border-left-color: #7367f0; }
  &--sent { border-left-color: #28c76f; }
  &--failed { border-left-color: #ea5455; }
  &--cancelled { border-left-color: #cbd5e1; opacity: .8; }
}
.wsl-batch__head { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; }
.wsl-when { font-size: .84rem; font-weight: 800; color: #1f2a44; }
.wsl-mode { font-size: .74rem; font-weight: 700; color: #1f9d57; i { margin-right: 4px; } }
.wsl-by { font-size: .7rem; color: #94a3b8; }
.wsl-status {
  font-size: .62rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; padding: 2px 8px; border-radius: 999px;
  background: rgba(100, 116, 139, .12); color: #64748b;
  &[data-status="PENDING"] { background: rgba(115, 103, 240, .12); color: #7367f0; }
  &[data-status="SENT"] { background: rgba(40, 199, 111, .14); color: #1f9d57; }
  &[data-status="FAILED"] { background: rgba(234, 84, 85, .14); color: #ea5455; }
}
.wsl-body { margin: 8px 0 0; font-size: .76rem; color: #475569; white-space: pre-wrap; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.wsl-people { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.wsl-person {
  display: inline-flex; align-items: center; gap: 5px; font-size: .7rem; font-weight: 700; padding: 3px 9px; border-radius: 999px;
  background: rgba(15, 23, 42, .05); color: #475569; text-transform: capitalize; cursor: default;
  &[data-status="SENT"] i { color: #1f9d57; }
  &[data-status="FAILED"] { background: rgba(234, 84, 85, .1); color: #b91c1c; }
  &[data-status="CANCELLED"] { text-decoration: line-through; opacity: .7; }
  &[data-status="PENDING"] i { color: #7367f0; }
}
</style>

<style lang="scss">
.dark-layout {
  .wsl-toggle { color: #94a3b8; }
  .wsl-tool { background: rgba(255, 255, 255, .06); color: #94a3b8; }
  .wsl-batch { background: #283046; border-color: rgba(255, 255, 255, .1); }
  .wsl-when { color: #e9ecf2; }
  .wsl-body { color: #b4b7bd; }
  .wsl-person { background: rgba(255, 255, 255, .08); color: #b4b7bd; }
}
</style>
